import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Popover,
} from '@mui/material';
import {
  ContentCopy,
  ContentCut,
  ContentPaste,
  Delete,
  Palette,
  Add,
  Remove,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { SketchPicker } from 'react-color';

import { useQueryClient } from 'react-query';
import {
  useUpdateShapeAnnotationMutation,
  useDeleteAnnotationMutation,
  useCreateAnnotationMutation,
} from './useAnnotationHooks';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { AnnotationEditorProps } from './types';
import { useAnnotationStore } from './annotationStore';
import { buildCreatePayloadFromAnnotation } from './utils';
import { LeaderAnchor } from '../../api/protosCompiled/annotation/annotation_pb';

export const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  annotation: initialAnnotation,
  position,
  onClose,
  onCopy,
  onCut,
  onPaste,
  onDelete,
  hasCopiedItems,
  isCutting = false,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const updateShapeMutation = useUpdateShapeAnnotationMutation();
  const deleteAnnotationMutation = useDeleteAnnotationMutation();
  const createAnnotationMutation = useCreateAnnotationMutation();

  // Source of truth: current annotation from the store
  const {
    currentAnnotation,
    copyAnnotation: copyFromStore,
    cutAnnotation: cutFromStore,
    setCopiedAnnotations,
    selectedShapeIds,
    isMultiSelectMode,
  } = useAnnotationStore();
  const annotation = currentAnnotation;

  // Track the editor's current position independently
  const [editorPosition, setEditorPosition] = useState<{
    x: number;
    y: number;
    dragged?: boolean;
    annotationId?: string;
  }>({ ...(position || { x: 100, y: 100 }), annotationId: annotation?.id });

  // Function to extract and normalize style properties from annotation
  const extractAnnotationStyle = useCallback((annotation: any) => {
    if (!annotation) return null;

    // Check both camelCase and lowercase properties (protobuf can use either)
    const style = annotation.style || {};

    // Helper function to get style value with fallbacks
    const getStyleValue = (camelKey: string, lowerKey: string, defaultValue: string) => {
      const value = style[camelKey] || style[lowerKey] || defaultValue;
      return value;
    };

    const extractedStyle = {
      fill: getStyleValue('fill', 'fill', 'rgba(255, 255, 255, 0.95)'),
      stroke: getStyleValue('stroke', 'stroke', '#1976d2'),
      strokeWidth: getStyleValue('strokeWidth', 'strokewidth', '2'),
      strokeDasharray: getStyleValue('strokeDasharray', 'strokedasharray', ''),
      opacity: getStyleValue('opacity', 'opacity', '1'),
      fontSize: getStyleValue('fontSize', 'fontsize', '14'),
      fontFamily: getStyleValue('fontFamily', 'fontfamily', 'Arial, sans-serif'),
      fontWeight: getStyleValue('fontWeight', 'fontweight', 'normal'),
      fontStyle: getStyleValue('fontStyle', 'fontstyle', 'normal'),
      textDecoration: getStyleValue('textDecoration', 'textdecoration', 'none'),
      // Additional properties that might exist
      textAnchor: getStyleValue('textAnchor', 'textanchor', 'start'),
    };

    return extractedStyle;
  }, []);

  // Highlighter toggle state (controls fill transparency). Default to true.
  const [isHighlighter, setIsHighlighter] = useState<boolean>(true);

  // Dimensional toggle state - manage locally for immediate UI feedback
  const [isDimensional, setIsDimensional] = useState<boolean>(false);

  // Local style state - initialize with annotation styles or defaults
  const [localStyle, setLocalStyle] = useState(() => {
    const extractedStyle = extractAnnotationStyle(annotation);
    return (
      extractedStyle || {
        fill: 'rgba(255, 255, 255, 0.95)',
        stroke: '#1976d2',
        strokeWidth: '2',
        strokeDasharray: '',
        opacity: '1',
        fontSize: '14',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        textAnchor: 'start',
      }
    );
  });

  // --- Fill color helpers for highlighter toggle ---
  const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const parseRgba = (color: string): { r: number; g: number; b: number; a: number } | null => {
    if (!color) return null;
    const rgbaMatch = color.match(
      /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i
    );
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1], 10);
      const g = parseInt(rgbaMatch[2], 10);
      const b = parseInt(rgbaMatch[3], 10);
      const a = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;
      return { r, g, b, a };
    }
    const hexMatch = color.match(/^#([0-9a-f]{6})$/i);
    if (hexMatch) {
      const intVal = parseInt(hexMatch[1], 16);
      const r = (intVal >> 16) & 255;
      const g = (intVal >> 8) & 255;
      const b = intVal & 255;
      return { r, g, b, a: 1 };
    }
    const shortHexMatch = color.match(/^#([0-9a-f]{3})$/i);
    if (shortHexMatch) {
      const h = shortHexMatch[1];
      const r = parseInt(h[0] + h[0], 16);
      const g = parseInt(h[1] + h[1], 16);
      const b = parseInt(h[2] + h[2], 16);
      return { r, g, b, a: 1 };
    }
    return null;
  };

  const rgbaString = (r: number, g: number, b: number, a: number) =>
    `rgba(${clamp255(r)}, ${clamp255(g)}, ${clamp255(b)}, ${Math.max(0, Math.min(1, a))})`;

  const rgbToHex = (r: number, g: number, b: number) =>
    `#${clamp255(r).toString(16).padStart(2, '0')}${clamp255(g)
      .toString(16)
      .padStart(2, '0')}${clamp255(b).toString(16).padStart(2, '0')}`.toLowerCase();

  const normalizeHex = (hex: string): string => {
    const six = hex.match(/^#([0-9a-f]{6})$/i);
    if (six) return `#${six[1].toLowerCase()}`;
    const three = hex.match(/^#([0-9a-f]{3})$/i);
    if (three) {
      const h = three[1].toLowerCase();
      return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
    }
    return '#ffffff';
  };

  const getBaseHexFromColor = (color: string): string => {
    if (!color) return '#ffffff';
    if (color === 'none') return 'none';
    const parsed = parseRgba(color);
    if (parsed) return rgbToHex(parsed.r, parsed.g, parsed.b);
    return normalizeHex(color);
  };

  const setAlpha = (color: string, alpha: number): string => {
    if (color === 'none') return color;
    const parsed = parseRgba(color);
    if (parsed) return rgbaString(parsed.r, parsed.g, parsed.b, alpha);
    // Fallback to white opaque if unrecognized
    return rgbaString(255, 255, 255, alpha);
  };

  const ensureOpacityBasedOnHighlighter = (color: string, highlighter: boolean): string => {
    if (!color || color === 'none') return color;
    return setAlpha(color, highlighter ? 0.2 : 1);
  };

  // Derive highlighter state only when switching to a different annotation
  useEffect(() => {
    const currentFill = (annotation?.style?.fill as string) || undefined;
    if (currentFill) {
      const parsed = parseRgba(currentFill);
      const alpha = parsed ? parsed.a : 1;
      setIsHighlighter(alpha < 1);
    } else {
      setIsHighlighter(true);
    }
    // Intentionally only derive on annotation switch to preserve user toggles across fill changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotation?.id]);

  // Initialize dimensional state when annotation changes
  useEffect(() => {
    setIsDimensional(annotation?.isdimensional || false);
  }, [annotation?.id, annotation?.isdimensional]);

  // Update local style when switching to a different annotation
  useEffect(() => {
    if (annotation?.id && annotation.id !== editorPosition.annotationId) {
      const extractedStyle = extractAnnotationStyle(annotation);
      if (extractedStyle) {
        setLocalStyle(extractedStyle);
      }
    }
  }, [annotation, editorPosition.annotationId, extractAnnotationStyle]);

  // Update local style when annotation changes (for selection switching)
  useEffect(() => {
    if (annotation?.id) {
      const extractedStyle = extractAnnotationStyle(annotation);
      if (extractedStyle) {
        setLocalStyle(extractedStyle);
      }
    }
  }, [annotation, extractAnnotationStyle]);

  // Update local style when switching to a different annotation id
  useEffect(() => {
    if (!annotation?.id) return;
    const extractedStyle = extractAnnotationStyle(annotation);
    extractedStyle && setLocalStyle(extractedStyle);
  }, [annotation, extractAnnotationStyle]);

  // Force refresh of annotation data to stay in sync with server state
  useEffect(() => {
    if (annotation?.id) {
      // This ensures we always have the latest server state
      queryClient.invalidateQueries(['annotations', annotation.objectId]);
    }
  }, [annotation?.id, annotation?.objectId, queryClient]);

  // Only update local style when switching to a different annotation
  // This prevents the infinite loop that was preventing style changes

  // Update local position when position prop changes (but only if we haven't dragged yet)
  useEffect(() => {
    if (position && !editorPosition.hasOwnProperty('dragged')) {
      setEditorPosition(prev => ({ ...prev, ...position }));
    }
  }, [position, editorPosition]);

  // Preserve editor position when annotation changes (but not when it's the same annotation)
  useEffect(() => {
    if (annotation?.id) {
      const currentAnnotationId = editorPosition.annotationId;
      if (currentAnnotationId !== annotation.id) {
        // New annotation - use the provided position
        setEditorPosition({ ...position, annotationId: annotation.id, dragged: false });
      } else if (!editorPosition.hasOwnProperty('dragged') || !editorPosition.dragged) {
        // Same annotation, but position prop changed (e.g., from cursor position update)
        // Only update if we haven't manually dragged the editor
        setEditorPosition(prev => ({ ...prev, ...position }));
      }
    }
  }, [annotation?.id, position, editorPosition]);

  // Apply style changes in real-time
  const handleStyleChange = (property: string, value: string | number) => {
    const newStyle = {
      ...localStyle,
      [property]: value.toString(),
    };
    setLocalStyle(newStyle);

    // Apply changes immediately for real-time preview
    if (annotation?.id) {
      // For text annotations, ensure we preserve the text content in geometry
      let updatedGeometry = annotation.geometry;
      if (annotation.type === 0 || annotation.type === AnnotationType.TEXT) {
        // AnnotationType.TEXT
        updatedGeometry = {
          ...annotation.geometry,
          text: annotation.geometry?.text || 'Click to edit',
        };
      }

      const mutationData = {
        id: annotation.id,
        objectId: annotation.objectId,
        type: annotation.type,
        geometry: updatedGeometry,
        style: newStyle,
        transform: annotation.transform, // Preserve existing transform
        layerId: (annotation.layerId as string) || 'default',
        zIndex: (annotation.zIndex as number) ?? 0,
      };

      updateShapeMutation.mutate(mutationData, {
        onSuccess: () => {},
        onError: error => {
          console.error('❌ Failed to save style to database:', error);
          console.error('❌ Error details:', {
            error,
            mutationData,
            annotation,
            property,
            value,
          });
        },
      });
    } else {
      console.warn('⚠️ Cannot update style - missing annotation ID:', {
        annotation,
        hasId: !!annotation?.id,
        hasObjectId: !!annotation?.objectId,
      });
    }
  };

  // Layer and ordering helpers
  const getCurrentLayerId = (): string => {
    return (annotation?.layerId as string) || 'default';
  };

  const getCurrentZIndex = (): number => {
    const z = (annotation?.zIndex as number) ?? 0;
    return Number.isFinite(z) ? z : 0;
  };

  const computeLayerExtents = (layerId: string): { minZ: number; maxZ: number } => {
    try {
      if (!annotation?.objectId) return { minZ: 0, maxZ: 0 };
      const cache: any = queryClient.getQueryData(['annotations', annotation.objectId]);
      const list: any[] = cache?.annotationsList || [];
      const zVals: number[] = list
        .map(a => {
          const ad = a.annotationdata || {};
          const lid = (ad as any).layerid || 'default';
          if (lid !== layerId) return null;
          const custom: Array<[string, string]> =
            ((ad as any)?.custompropertiesMap as Array<[string, string]>) || [];
          const zStr = custom.find(([k]) => k === 'zIndex')?.[1];
          const zNum = zStr !== undefined ? parseInt(zStr, 10) : 0;
          return Number.isFinite(zNum) ? zNum : 0;
        })
        .filter((v): v is number => v !== null);
      if (zVals.length === 0) return { minZ: 0, maxZ: 0 };
      return { minZ: Math.min(...zVals), maxZ: Math.max(...zVals) };
    } catch {
      return { minZ: 0, maxZ: 0 };
    }
  };

  const setLayerAndZ = (layerId?: string, zIndex?: number) => {
    if (!annotation?.id) return;
    const payload = {
      id: annotation.id,
      objectId: annotation.objectId,
      type: annotation.type,
      geometry: annotation.geometry,
      // include current style so recent changes are preserved across non-style updates
      style: localStyle as any,
      transform: annotation.transform,
      layerId: layerId ?? getCurrentLayerId(),
      zIndex: zIndex ?? getCurrentZIndex(),
    };
    updateShapeMutation.mutate(payload, { onSuccess: () => {} });
  };

  // Helper: get selected annotations from cache
  const getSelectedAnnotations = (): any[] => {
    const base = annotation?.objectId;
    if (!base) return annotation ? [annotation] : [];
    const cache: any = queryClient.getQueryData(['shapeAnnotations', base]);
    const list: any[] = cache?.annotations || cache?.annotationsList || [];
    const ids =
      isMultiSelectMode && selectedShapeIds && selectedShapeIds.length > 0
        ? selectedShapeIds
        : annotation?.id
          ? [annotation.id]
          : [];
    const byId = new Map(list.map(a => [a.id, a]));
    return ids.map(id => byId.get(id)).filter(Boolean);
  };

  // Handle copy/cut/paste/delete actions
  const handleAction = async (action: 'copy' | 'cut' | 'paste' | 'delete') => {
    switch (action) {
      case 'copy':
        {
          const selected = getSelectedAnnotations();
          if (selected.length > 0) {
            setCopiedAnnotations(selected);
          } else if (annotation) {
            copyFromStore(annotation);
          }
        }
        onCopy();
        break;
      case 'cut':
        {
          const selected = getSelectedAnnotations();
          if (selected.length > 0) {
            setCopiedAnnotations(selected);
            // Delete all selected
            for (const ann of selected) {
              if (ann?.id && ann?.objectId) {
                try {
                  await deleteAnnotationMutation.mutateAsync({
                    id: ann.id,
                    objectId: ann.objectId,
                  });
                } catch (err) {
                  console.error('❌ Failed to cut annotation:', err);
                }
              }
            }
          } else if (annotation) {
            cutFromStore(annotation);
          }
        }
        onCut();
        break;
      case 'paste':
        {
          if (!annotation?.objectId) {
            onPaste();
            break;
          }
          const store = (useAnnotationStore as any).getState?.() || useAnnotationStore;
          const copied: any[] = store.copiedAnnotations || [];
          if (Array.isArray(copied) && copied.length > 0) {
            // Create all on current object, offsetting positions
            for (let i = 0; i < copied.length; i++) {
              const payload = buildCreatePayloadFromAnnotation(copied[i], annotation.objectId, i);
              try {
                await createAnnotationMutation.mutateAsync(payload);
              } catch (err) {
                console.error('❌ Failed to paste annotation:', err);
              }
            }
          } else {
            onPaste();
          }
        }
        break;
      case 'delete':
        {
          const selected = getSelectedAnnotations();
          if (selected.length > 0) {
            for (const ann of selected) {
              if (ann?.id && ann?.objectId) {
                try {
                  await deleteAnnotationMutation.mutateAsync({
                    id: ann.id,
                    objectId: ann.objectId,
                  });
                } catch (error) {
                  console.error('❌ Failed to delete annotation:', error);
                }
              }
            }
            onDelete();
          } else if (annotation?.id && annotation?.objectId) {
            try {
              await deleteAnnotationMutation.mutateAsync({
                id: annotation.id,
                objectId: annotation.objectId,
              });
              onDelete();
            } catch (error) {
              console.error('❌ Failed to delete annotation:', error);
            }
          } else {
            console.warn('⚠️ Cannot delete annotation: missing id or objectId', {
              id: annotation?.id,
              objectId: annotation?.objectId,
            });
            onDelete();
          }
        }
        break;
    }
  };

  // Leader helpers - minimal add/delete UX
  const getAnchorLabel = (anchor: LeaderAnchor) => {
    switch (anchor) {
      case LeaderAnchor.NW:
        return 'Northwest (NW)';
      case LeaderAnchor.N:
        return 'North (N)';
      case LeaderAnchor.NE:
        return 'Northeast (NE)';
      case LeaderAnchor.E:
        return 'East (E)';
      case LeaderAnchor.SE:
        return 'Southeast (SE)';
      case LeaderAnchor.S:
        return 'South (S)';
      case LeaderAnchor.SW:
        return 'Southwest (SW)';
      case LeaderAnchor.W:
        return 'West (W)';
      case LeaderAnchor.C:
        return 'Center (C)';
      default:
        return 'Unknown Anchor';
    }
  };

  // Helper to read leaders from any proto-backed path on the annotation
  const readLeadersFromAnnotation = (ann: any): any[] => {
    const a = ann || {};
    const ad = a.annotationdata || a.annotationData || {};
    const gFromAd = ad.geometry || {};
    const g = a.geometry || {};
    const list = gFromAd.leadersList || gFromAd.leaders || g.leadersList || g.leaders || [];
    return Array.isArray(list) ? list : [];
  };

  // Local leaders state to avoid stale index-based deletes rehydrating removed items
  const [localLeaders, setLocalLeaders] = useState<any[]>(readLeadersFromAnnotation(annotation));

  // Color picker popover states
  const [fillColorPickerAnchor, setFillColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [strokeColorPickerAnchor, setStrokeColorPickerAnchor] = useState<HTMLElement | null>(null);

  // Sync localLeaders whenever the active annotation changes or its geometry reference updates
  useEffect(() => {
    setLocalLeaders(readLeadersFromAnnotation(annotation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotation?.id, annotation?.geometry]);

  const getCurrentLeaders = () => localLeaders;

  const handleAddLeader = () => {
    if (!annotation?.id) return;
    // Start add-leader flow and close the editor per UX spec
    const store = (useAnnotationStore as any).getState?.() || useAnnotationStore;
    store.startAddLeader?.(annotation.id);
    onClose();
  };

  const handleDeleteLeader = (index: number) => {
    if (!annotation?.id) return;
    const existing: any[] = localLeaders;
    const updated = existing.filter((_, i) => i !== index);
    updateShapeMutation.mutate(
      {
        id: annotation.id,
        objectId: annotation.objectId,
        type: annotation.type,
        geometry: { ...(annotation.geometry || {}), leaders: updated },
        style: annotation.style,
        transform: annotation.transform,
        layerId: (annotation as any).layerId || 'default',
        zIndex: (annotation as any).zIndex ?? 0,
      },
      {
        onSuccess: () => {
          // Optimistically reflect removal to prevent rehydrating previously deleted items
          setLocalLeaders(updated);
          // Also refresh server state
          queryClient.invalidateQueries(['annotations', annotation.objectId]);
        },
      }
    );
  };

  return (
    <Paper
      onContextMenu={e => e.preventDefault()}
      elevation={4}
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        padding: 2,
        width: '100%',
        borderRadius: 1,
        border: `3px solid ${theme.palette.primary.main}`,
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'default',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        maxHeight: '85vh', // Limit height to 80% of viewport
        overflow: 'hidden', // Hide overflow on the container
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Header - Fixed at top */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Palette fontSize="small" />
            Style Editor
          </Typography>
          {isCutting && (
            <Box
              sx={{
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              CUT MODE
            </Box>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1 }}>
            ×
          </Typography>
        </IconButton>
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1, // Add some padding for scrollbar
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.divider, 0.1),
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.primary.main, 0.3),
            borderRadius: '4px',
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.5),
            },
          },
        }}
      >
        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Cut (Ctrl+X)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCut />}
              onClick={() => handleAction('cut')}
              sx={{ flex: 1 }}
            >
              Cut
            </Button>
          </Tooltip>
          <Tooltip title="Copy (Ctrl+C)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => handleAction('copy')}
              sx={{ flex: 1 }}
            >
              Copy
            </Button>
          </Tooltip>
          <Tooltip title="Paste (Ctrl+V)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentPaste />}
              onClick={() => handleAction('paste')}
              disabled={!hasCopiedItems}
              sx={{ flex: 1 }}
            >
              Paste
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => {
                handleAction('delete');
              }}
              sx={{ flex: 1 }}
            >
              Delete
            </Button>
          </Tooltip>
        </Stack>

        <Divider sx={{ mb: 2 }} />
        {/* Fill Color */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Fill Color | Highlighter
            </Typography>
            <input
              type="checkbox"
              id="highlighter-checkbox"
              checked={isHighlighter}
              onChange={e => {
                const checked = e.target.checked;
                setIsHighlighter(checked);
                const newFill = ensureOpacityBasedOnHighlighter(localStyle.fill, checked);
                handleStyleChange('fill', newFill);
              }}
              style={{ marginRight: '4px' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Dimensional
            </Typography>
            <input
              type="checkbox"
              id="dimensional-checkbox"
              checked={isDimensional}
              onChange={e => {
                const checked = e.target.checked;

                // Update local state immediately for UI feedback
                setIsDimensional(checked);

                // Update the annotation's isdimensional property
                if (annotation?.id && annotation?.objectId) {
                  const mutationData = {
                    id: annotation.id,
                    objectId: annotation.objectId,
                    type: annotation.type,
                    geometry: annotation.geometry,
                    style: localStyle,
                    transform: annotation.transform,
                    layerId: (annotation.layerId as string) || 'default',
                    zIndex: (annotation.zIndex as number) ?? 0,
                    isdimensional: checked,
                  };

                  updateShapeMutation.mutate(mutationData, {
                    onSuccess: result => {},
                    onError: error => {
                      console.error('❌ Failed to update dimensional property:', error);
                      // Revert local state on error
                      setIsDimensional(!checked);
                    },
                  });
                } else {
                  console.warn('⚠️ Cannot update dimensional property: missing id or objectId', {
                    id: annotation?.id,
                    objectId: annotation?.objectId,
                  });
                  // Revert local state if we can't update
                  setIsDimensional(!checked);
                }
              }}
              style={{ marginRight: '4px' }}
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={e => setFillColorPickerAnchor(e.currentTarget)}
              sx={{
                backgroundColor: localStyle.fill === 'none' ? 'transparent' : localStyle.fill,
                borderColor: theme.palette.divider,
                minWidth: '60px',
                height: '36px',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {localStyle.fill === 'none' ? 'None' : 'Pick'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                handleStyleChange('fill', 'none');
              }}
              sx={{ minWidth: '60px' }}
            >
              None
            </Button>
          </Stack>

          <Popover
            open={Boolean(fillColorPickerAnchor)}
            anchorEl={fillColorPickerAnchor}
            onClose={() => setFillColorPickerAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <SketchPicker
              color={getBaseHexFromColor(localStyle.fill)}
              onChange={(color: any) => {
                if (color.hex) {
                  // Preserve current highlighter state during color change
                  const adjusted = setAlpha(color.hex, isHighlighter ? 0.2 : 1);
                  handleStyleChange('fill', adjusted);
                }
              }}
              onChangeComplete={(color: any) => {
                if (color.hex) {
                  // Preserve current highlighter state during color change
                  const adjusted = setAlpha(color.hex, isHighlighter ? 0.2 : 1);
                  handleStyleChange('fill', adjusted);
                }
              }}
            />
          </Popover>
        </Box>

        {/* Stroke Color and Line Style Row */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {/* Stroke Color */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Stroke Color
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={e => setStrokeColorPickerAnchor(e.currentTarget)}
                sx={{
                  backgroundColor: localStyle.stroke,
                  borderColor: theme.palette.divider,
                  minWidth: '60px',
                  height: '36px',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  handleStyleChange('stroke', '#1976d2');
                }}
                sx={{ minWidth: '60px' }}
              >
                Reset
              </Button>
            </Stack>

            <Popover
              open={Boolean(strokeColorPickerAnchor)}
              anchorEl={strokeColorPickerAnchor}
              onClose={() => setStrokeColorPickerAnchor(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <SketchPicker
                color={localStyle.stroke}
                onChange={(color: any) => {
                  if (color.hex) {
                    handleStyleChange('stroke', color.hex);
                  }
                }}
                onChangeComplete={(color: any) => {
                  if (color.hex) {
                    handleStyleChange('stroke', color.hex);
                  }
                }}
              />
            </Popover>
          </Box>

          {/* Line Style */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Line Style
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={localStyle.strokeDasharray}
                onChange={e => handleStyleChange('strokeDasharray', e.target.value)}
                size="small"
              >
                <MenuItem value="">Solid</MenuItem>
                <MenuItem value="5,5">Dashed</MenuItem>
                <MenuItem value="2,2">Dotted</MenuItem>
                <MenuItem value="10,5">Long Dash</MenuItem>
                <MenuItem value="5,2,2,2">Dash-Dot</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Stroke Width */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Stroke Width: {localStyle.strokeWidth}px
          </Typography>
          <Slider
            value={parseFloat(localStyle.strokeWidth)}
            onChange={(_, value) => handleStyleChange('strokeWidth', value.toString())}
            min={0}
            max={10}
            step={0.5}
            marks
            size="small"
          />
        </Box>

        {/* Opacity */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Opacity: {Math.round(parseFloat(localStyle.opacity) * 100)}%
          </Typography>
          <Slider
            value={parseFloat(localStyle.opacity)}
            onChange={(_, value) => handleStyleChange('opacity', value.toString())}
            min={0}
            max={1}
            step={0.1}
            marks
            size="small"
          />
        </Box>

        {/* Layer and Order */}
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Layering
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const { maxZ } = computeLayerExtents(getCurrentLayerId());
              setLayerAndZ(undefined, maxZ + 1); // Bring to front
            }}
          >
            Bring to Front
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const { minZ } = computeLayerExtents(getCurrentLayerId());
              setLayerAndZ(undefined, minZ - 1); // Send to Back
            }}
          >
            Send to Back
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setLayerAndZ(undefined, getCurrentZIndex() + 1)}
          >
            Bring Forward
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setLayerAndZ(undefined, getCurrentZIndex() - 1)}
          >
            Send Backward
          </Button>
        </Stack>

        {/* Font Settings (for text annotations) */}
        {(annotation?.type === 0 || annotation?.type === AnnotationType.TEXT) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Text Settings
            </Typography>
            {/* Font Size */}
            <TextField
              label="Font Size"
              type="number"
              size="small"
              value={localStyle.fontSize || '14'}
              onChange={e => {
                handleStyleChange('fontSize', e.target.value);
              }}
              inputProps={{ min: 8, max: 72 }}
              sx={{ mb: 2, mr: 1 }}
            />

            {/* Font Family */}
            <FormControl size="small" sx={{ mb: 2 }}>
              <InputLabel>Font Family</InputLabel>
              <Select
                value={localStyle.fontFamily}
                onChange={e => handleStyleChange('fontFamily', e.target.value)}
                label="Font Family"
              >
                <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                <MenuItem value="Helvetica, Arial, sans-serif">Helvetica</MenuItem>
                <MenuItem value="Times New Roman, serif">Times New Roman</MenuItem>
                <MenuItem value="Georgia, serif">Georgia</MenuItem>
                <MenuItem value="Verdana, sans-serif">Verdana</MenuItem>
                <MenuItem value="Courier New, monospace">Courier New</MenuItem>
              </Select>
            </FormControl>

            {/* Font Style Checkboxes */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Font Style
              </Typography>
              <Stack direction="row" spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="bold-checkbox"
                    checked={localStyle.fontWeight === 'bold'}
                    onChange={e =>
                      handleStyleChange('fontWeight', e.target.checked ? 'bold' : 'normal')
                    }
                    style={{ marginRight: '4px' }}
                  />
                  <label htmlFor="bold-checkbox" style={{ fontSize: '12px' }}>
                    Bold
                  </label>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="italic-checkbox"
                    checked={localStyle.fontStyle === 'italic'}
                    onChange={e =>
                      handleStyleChange('fontStyle', e.target.checked ? 'italic' : 'normal')
                    }
                    style={{ marginRight: '4px' }}
                  />
                  <label htmlFor="italic-checkbox" style={{ fontSize: '12px' }}>
                    Italic
                  </label>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="underline-checkbox"
                    checked={localStyle.textDecoration === 'underline'}
                    onChange={e =>
                      handleStyleChange('textDecoration', e.target.checked ? 'underline' : 'none')
                    }
                    style={{ marginRight: '4px' }}
                  />
                  <label htmlFor="underline-checkbox" style={{ fontSize: '12px' }}>
                    Underline
                  </label>
                </Box>
              </Stack>
            </Box>
          </>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const extractedStyle = extractAnnotationStyle(annotation);
              if (extractedStyle) {
                setLocalStyle(extractedStyle);

                // Apply reset immediately
                if (annotation?.id) {
                  updateShapeMutation.mutate(
                    {
                      id: annotation.id,
                      objectId: annotation.objectId,
                      type: annotation.type,
                      geometry: annotation.geometry,
                      style: extractedStyle,
                      transform: annotation.transform, // Preserve existing transform
                    },
                    {
                      onSuccess: () => {},
                      onError: error => {
                        console.error('❌ Failed to save reset style to database:', error);
                      },
                    }
                  );
                }
              }
            }}
            sx={{ flex: 1 }}
          >
            Reset to Original
          </Button>
        </Stack>

        {/* Leader Arrows Section */}
        {annotation?.type === 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Leader Arrows
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddLeader}
                sx={{ flex: 1 }}
              >
                Add Leader
              </Button>
            </Stack>

            {/* Leader dropdown menu */}
            {getCurrentLeaders().length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Leaders ({getCurrentLeaders().length})
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Select to manage</InputLabel>
                  <Select
                    value=""
                    label="Select to manage"
                    onChange={() => {}} // Prevent selection, just show menu
                  >
                    {getCurrentLeaders().map((leader, index) => (
                      <MenuItem key={`${annotation?.id}-leader-${index}`} value={`leader-${index}`}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Leader {index + 1}</Typography>
                            <Chip
                              label={getAnchorLabel(leader.anchor)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteLeader(index);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {getCurrentLeaders().length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No leader arrows added yet. Click "Add Leader" to create one.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
